import { Link } from 'react-router-dom';

export default function ContentSection() {
  return (
    <section id="guides" className="py-16 md:py-20 bg-background-100">
      <div className="max-w-4xl mx-auto px-4 md:px-6">
        <div className="mb-10">
          <span className="inline-block px-3 py-1 text-xs font-medium text-primary-600 bg-primary-50 rounded-full mb-4">
            Buying Guide
          </span>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-6">
            What Is the VigorBuy Spreadsheet?
          </h2>
          <p className="text-foreground-700 leading-relaxed mb-4">
            The <strong>VigorBuy Spreadsheet</strong> is a comprehensive, regularly updated resource designed to help buyers discover, compare, 
            and purchase products with confidence. Whether you are looking for sneakers, hoodies, accessories, or 
            hard-to-find items, the VigorBuy Spreadsheet organizes everything into easy-to-navigate categories with 
            direct links to trusted sources.
          </p>
          <p className="text-foreground-700 leading-relaxed mb-4">
            Unlike scattered blog posts or outdated forum threads, the <strong>VigorBuy Spreadsheet</strong> is 
            maintained daily by a dedicated team that verifies product availability, updates pricing information, and 
            adds new finds. The spreadsheet format makes it incredibly efficient — you can scan hundreds of products 
            in minutes rather than hours.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
          <div className="bg-background-50 rounded-2xl p-6 border border-background-200/70">
            <div className="w-10 h-10 rounded-xl bg-secondary-100 flex items-center justify-center mb-4">
              <i className="ri-search-line text-secondary-600 text-lg"></i>
            </div>
            <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-2">Discover Products Faster</h3>
            <p className="text-sm text-foreground-600 leading-relaxed">
              Browse organized product collections with the VigorBuy Spreadsheet and find exactly what you need in seconds.
            </p>
          </div>
          <div className="bg-background-50 rounded-2xl p-6 border border-background-200/70">
            <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center mb-4">
              <i className="ri-time-line text-accent-600 text-lg"></i>
            </div>
            <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-2">Save Valuable Time</h3>
            <p className="text-sm text-foreground-600 leading-relaxed">
              Filter through hundreds of verified products in the VigorBuy Spreadsheet without endless scrolling.
            </p>
          </div>
          <div className="bg-background-50 rounded-2xl p-6 border border-background-200/70">
            <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center mb-4">
              <i className="ri-shield-check-line text-primary-600 text-lg"></i>
            </div>
            <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-2">Shop More Safely</h3>
            <p className="text-sm text-foreground-600 leading-relaxed">
              Every listing in the VigorBuy Spreadsheet is verified, so you can purchase with peace of mind.
            </p>
          </div>
        </div>

        {/* How It Works */}
        <div className="mb-10">
          <h3 className="font-heading text-2xl font-bold text-foreground-950 mb-6">How to Use the VigorBuy Spreadsheet</h3>
          <div className="space-y-5">
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-500 text-background-50 flex items-center justify-center text-sm font-bold">1</div>
              <div>
                <h4 className="font-semibold text-foreground-900 mb-1">Open the VigorBuy Spreadsheet</h4>
                <p className="text-sm text-foreground-600">Access the latest version through the main website. The spreadsheet is updated daily with new products and categories.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-500 text-background-50 flex items-center justify-center text-sm font-bold">2</div>
              <div>
                <h4 className="font-semibold text-foreground-900 mb-1">Browse by Category</h4>
                <p className="text-sm text-foreground-600">Navigate through organized tabs for Shoes, Hoodies, Jackets, Accessories, and more. Each category contains verified product listings.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-500 text-background-50 flex items-center justify-center text-sm font-bold">3</div>
              <div>
                <h4 className="font-semibold text-foreground-900 mb-1">Compare and Select</h4>
                <p className="text-sm text-foreground-600">Review product details, pricing, and availability within the VigorBuy Spreadsheet before making your purchase decision.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-500 text-background-50 flex items-center justify-center text-sm font-bold">4</div>
              <div>
                <h4 className="font-semibold text-foreground-900 mb-1">Purchase with Confidence</h4>
                <p className="text-sm text-foreground-600">Use the direct links provided in the VigorBuy Spreadsheet to complete your purchase on trusted platforms.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Safety Section */}
        <div id="safety" className="mb-10">
          <h3 className="font-heading text-2xl font-bold text-foreground-950 mb-6">Is the VigorBuy Spreadsheet Safe to Use?</h3>
          <p className="text-foreground-700 leading-relaxed mb-4">
            Yes — the <strong>VigorBuy Spreadsheet</strong> is a safe and reliable resource. Every product listing is 
            manually verified by the team before being added. The spreadsheet does not collect personal information, 
            and all purchases are completed on external, trusted platforms. You maintain full control over your 
            transactions while benefiting from organized product discovery.
          </p>
          <p className="text-foreground-700 leading-relaxed mb-4">
            The VigorBuy Spreadsheet also includes safety tips and shipping guidance to help you avoid common 
            pitfalls when shopping online. From payment security recommendations to tracking your orders, 
            the resource is designed to make your buying experience as smooth and secure as possible.
          </p>
          <div className="flex flex-wrap gap-3 mt-6">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-secondary-100 text-secondary-700 rounded-full text-xs font-medium">
              <i className="ri-check-line"></i> Verified Products
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-secondary-100 text-secondary-700 rounded-full text-xs font-medium">
              <i className="ri-check-line"></i> Daily Updates
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-secondary-100 text-secondary-700 rounded-full text-xs font-medium">
              <i className="ri-check-line"></i> Safety Tips
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-secondary-100 text-secondary-700 rounded-full text-xs font-medium">
              <i className="ri-check-line"></i> Shipping Guides
            </span>
          </div>
        </div>

        {/* Comparison Table */}
        <div className="mb-10">
          <h3 className="font-heading text-2xl font-bold text-foreground-950 mb-6">Why Choose the VigorBuy Spreadsheet?</h3>
          <div className="overflow-x-auto rounded-2xl border border-background-200/70">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-background-200/50">
                  <th className="text-left px-6 py-4 font-semibold text-foreground-800">Feature</th>
                  <th className="text-center px-6 py-4 font-semibold text-primary-600">VigorBuy Spreadsheet</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-200/50">
                <tr>
                  <td className="px-6 py-3.5 text-foreground-700">Organized Product Guides</td>
                  <td className="px-6 py-3.5 text-center text-secondary-600 font-semibold">✓ Yes</td>
                </tr>
                <tr className="bg-background-50">
                  <td className="px-6 py-3.5 text-foreground-700">Daily Updates</td>
                  <td className="px-6 py-3.5 text-center text-secondary-600 font-semibold">✓ Yes</td>
                </tr>
                <tr>
                  <td className="px-6 py-3.5 text-foreground-700">Safety Recommendations</td>
                  <td className="px-6 py-3.5 text-center text-secondary-600 font-semibold">✓ Yes</td>
                </tr>
                <tr className="bg-background-50">
                  <td className="px-6 py-3.5 text-foreground-700">Verified Product Listings</td>
                  <td className="px-6 py-3.5 text-center text-secondary-600 font-semibold">✓ Yes</td>
                </tr>
                <tr>
                  <td className="px-6 py-3.5 text-foreground-700">Beginner-Friendly Tutorials</td>
                  <td className="px-6 py-3.5 text-center text-secondary-600 font-semibold">✓ Yes</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center bg-background-50 rounded-2xl p-8 md:p-10 border-2 border-background-200/70">
          <Link
            to="/guide/vigorbuy-spreadsheet-complete-guide"
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 text-background-50 font-bold rounded-full hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
          >
            Read the Full Buying Guide
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}